import type { OverviewAge, OverviewCategory } from '../../../services/dmMonitoringOverview';
import { plural } from '../../../utils/plural.ts';

const SECOND_MS = 1000;
const MINUTE_S = 60;
const HOUR_S = 60 * MINUTE_S;
const DAY_S = 24 * HOUR_S;

function wholeUnits(seconds: number, unit: number): number {
    return Math.floor(seconds / unit);
}

// Two units at most: "1 d 4 h", "3 h 20 min", "45 min"; a zero second unit is dropped.
export function formatDuration(seconds: number | null): string | null {
    if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return null;
    if (seconds < MINUTE_S) return 'Under 1 min';
    const days = wholeUnits(seconds, DAY_S);
    const hours = wholeUnits(seconds % DAY_S, HOUR_S);
    const minutes = wholeUnits(seconds % HOUR_S, MINUTE_S);
    if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
    if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
    return `${minutes} min`;
}

function secondsSince(iso: string, now: number): number | null {
    const time = new Date(iso).getTime();
    if (Number.isNaN(time)) return null;
    return Math.max(0, (now - time) / SECOND_MS);
}

// Age of an open item at a glance: "9 days", "5 h", "20 min".
export function formatAge(iso: string, now: number): string | null {
    const seconds = secondsSince(iso, now);
    if (seconds === null) return null;
    if (seconds >= DAY_S) return plural(wholeUnits(seconds, DAY_S), 'day');
    if (seconds >= HOUR_S) return `${wholeUnits(seconds, HOUR_S)} h`;
    if (seconds >= MINUTE_S) return `${wholeUnits(seconds, MINUTE_S)} min`;
    return 'Under 1 min';
}

export function formatUpdatedAgo(iso: string, now: number): string {
    const seconds = secondsSince(iso, now);
    if (seconds === null || seconds < MINUTE_S) return 'just now';
    if (seconds < HOUR_S) return `${wholeUnits(seconds, MINUTE_S)} min ago`;
    return `${wholeUnits(seconds, HOUR_S)} h ago`;
}

export interface OldestItem {
    category: OverviewCategory;
    at: string;
}

// The older of the two; a tie goes to the notification.
export function pickOldestItem(age: OverviewAge): OldestItem | null {
    const candidates: OldestItem[] = [
        ...(age.oldestNotificationAt
            ? [{ category: 'notification' as const, at: age.oldestNotificationAt }]
            : []),
        ...(age.oldestMessageAt ? [{ category: 'message' as const, at: age.oldestMessageAt }] : [])
    ];
    return candidates.reduce<OldestItem | null>(
        (oldest, item) =>
            oldest === null || new Date(item.at).getTime() < new Date(oldest.at).getTime()
                ? item
                : oldest,
        null
    );
}
